import { type QuoteLine } from '@kalos/kalos-rpc';
import { Button, Card, Checkbox, Input } from '@kalos/ui';
import { type FC, useCallback, useEffect, useRef } from 'react';
import SignaturePad from 'react-signature-canvas';

import { getLineDiscount } from './discount';

interface CustomerProposalSignProps {
  selectedServices: QuoteLine[];
  proposalValidity: {
    validUntil: Date;
    agreementDuration: Date;
  };
  preparedByName: string;
  acceptTerms: boolean;
  onAcceptTermsChange: (value: boolean) => void;
  acceptedBy: string;
  onAcceptedByChange: (value: string) => void;
  signature: string;
  onSignatureChange: (value: string) => void;
  discount?: string; // Discount percentage from event
  discountCost?: string; // Flat discount amount from event
  taxRate?: string; // Tax rate percentage from event
}

export const CustomerProposalSign: FC<CustomerProposalSignProps> = ({
  selectedServices,
  proposalValidity,
  preparedByName,
  acceptTerms,
  onAcceptTermsChange,
  acceptedBy,
  onAcceptedByChange,
  signature,
  onSignatureChange,
  discount,
  discountCost,
  taxRate: taxRateProp,
}) => {
  const signaturePadRef = useRef<SignaturePad>(null);
  const isSavingRef = useRef(false);

  // Restore signature when signature prop changes
  useEffect(() => {
    if (signature && signaturePadRef.current && !isSavingRef.current) {
      // Use a small delay to ensure canvas is fully rendered
      const timeoutId = setTimeout(() => {
        if (signaturePadRef.current) {
          // Get the canvas and context
          const canvas = signaturePadRef.current.getCanvas();
          const ctx = canvas.getContext('2d');

          if (!ctx) return;

          // Store current dimensions
          const { width, height } = canvas;

          // Clear and set up the canvas with proper scaling
          signaturePadRef.current.clear();

          // Create a new image to load the signature
          const img = new Image();
          img.onload = () => {
            // Clear the canvas again and draw the image at the correct size
            ctx.clearRect(0, 0, width, height);
            ctx.drawImage(img, 0, 0, width, height);
          };
          img.src = signature;
        }
      }, 50);

      return () => clearTimeout(timeoutId);
    }
  }, [signature]);

  // Calculate totals
  const subtotal = selectedServices.reduce((sum, service) => {
    const price = parseFloat(service.adjustment || '0');
    const quantity = service.quotedQuantity || 1;
    return sum + price * quantity;
  }, 0);

  // Calculate discount - EITHER flat discount OR percentage discount (flat takes priority)
  const discountPercentValue = parseFloat(discount || '0');
  const discountCostValue = parseFloat(discountCost || '0');
  const discountAmount =
    discountCostValue > 0
      ? discountCostValue
      : selectedServices.reduce(
          (sum, service) =>
            sum +
            getLineDiscount(
              parseFloat(service.adjustment || '0') * (service.quotedQuantity || 1),
              discountPercentValue,
              discountCostValue,
              service.excludeInvoiceDiscount,
            ),
          0,
        );
  const afterDiscount = Math.max(0, subtotal - discountAmount);

  // Calculate tax
  const parsedTaxRate = parseFloat(taxRateProp || '0') || 0;
  const taxAmount = afterDiscount * (parsedTaxRate / 100);
  const total = afterDiscount + taxAmount;

  const handleSignatureClear = useCallback(() => {
    if (signaturePadRef.current) {
      signaturePadRef.current.clear();
      onSignatureChange('');
    }
  }, [onSignatureChange]);

  const handleSignatureSave = useCallback(() => {
    if (signaturePadRef.current) {
      isSavingRef.current = true;
      // Save with consistent dimensions and quality
      const signatureData = signaturePadRef.current.toDataURL('image/png', 1.0);
      onSignatureChange(signatureData);
      // Reset the flag after a short delay to allow the prop update to complete
      setTimeout(() => {
        isSavingRef.current = false;
      }, 100);
    }
  }, [onSignatureChange]);

  return (
    <div className="space-y-6">
      {/* Terms & Conditions */}
      <Card className="bg-white p-6" id="terms-section">
        <h2 className="mb-4 text-xl font-semibold">Terms & Conditions</h2>
        <div className="mb-4 max-h-60 overflow-y-auto rounded border p-4">
          <div className="prose max-w-none text-sm">
            <h3 className="mb-3 font-bold">TERMS AND CONDITIONS</h3>

            <p className="mb-0">
              The undersigned has authority to order the above labor and materials on behalf of the
              above named purchaser. The labor and materials described above have been completely
              and satisfactorily performed and furnished. Kalos Services is not liable for any
              defects in labor or material unless the purchaser gives written notice of such defects
              within 30 days from the date of this contract. Payment in full is due upon receipt of
              this invoice and payable upon completion of work. The purchaser should be aware that
              under Florida law, Kalos Services has the right to file a mechanics lien if payment is
              not made per the terms of this document. Purchaser agrees to pay all costs of
              collection including a reasonable attorney&apos;s fee, in case this contract is not
              paid in full when due and the same is placed in the hands of an attorney for
              collection, foreclosure or repossession, whether suit be brought or not. All
              delinquent payments shall bear a service charge of 2% per month until paid. Selling
              price of all parts and equipment are on an exchange basis with the purchasers old
              parts and equipment. Warranty on all parts per manufacturers warranty policies 90 day
              labor guarantee: only on that portion previously serviced. If my payment is by check
              and my check is returned for any reason, I authorize the merchant to electronically
              debit my account for the amount of this item plus any fees allowed by law
            </p>
          </div>
        </div>
        <div className="flex items-start space-x-2">
          <Checkbox checked={acceptTerms} onCheckedChange={onAcceptTermsChange} />
          <div className="space-y-1 leading-none">
            <label className="text-sm font-medium">I accept the terms and conditions</label>
          </div>
        </div>
      </Card>

      {/* Signature Section */}
      <Card className="bg-white p-6" id="signature-section">
        <h2 className="mb-4 text-xl font-semibold">
          Signature <span className="text-red-600">*</span>
        </h2>
        <div
          className="mb-4 flex justify-center overflow-auto rounded border p-2"
          id="signature-canvas"
        >
          <SignaturePad
            ref={signaturePadRef}
            canvasProps={{
              width: 400, // Fixed width
              height: 200, // Fixed height
              className: 'border rounded',
              style: {
                touchAction: 'none',
                willChange: 'transform',
                WebkitUserSelect: 'none',
                WebkitTouchCallout: 'none',
                WebkitTapHighlightColor: 'transparent',
                minWidth: '400px',
                minHeight: '200px',
                display: 'block',
              },
            }}
            onEnd={handleSignatureSave}
            clearOnResize={false}
            dotSize={1}
            minWidth={0.5}
            maxWidth={2.5}
            throttle={16}
            backgroundColor="rgba(255,255,255,1)"
          />
        </div>
        <div className="mb-4 flex justify-end">
          <Button
            type="button"
            variant="outline"
            onClick={handleSignatureClear}
            className="bg-white"
          >
            Clear Signature
          </Button>
        </div>
        {/* Names and Date */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Proposed By</label>
            <div className="rounded bg-gray-100 px-3 py-2 text-gray-700">{preparedByName}</div>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              Signed By <span className="text-red-600">*</span>
            </label>
            <Input
              id="signed-by-input"
              value={acceptedBy}
              onChange={(e) => onAcceptedByChange(e.target.value)}
              className="bg-white"
              required
            />
          </div>
        </div>
        <div className="mt-4">
          <label className="mb-1 block text-sm font-medium text-gray-700">Date</label>
          <div className="rounded bg-gray-100 px-3 py-2 text-gray-700">
            {new Date().toLocaleDateString()}
          </div>
        </div>
      </Card>

      {/* Order Summary */}
      <Card
        className="mb-12 overflow-hidden border-l-4 bg-white p-6"
        style={{ borderLeftColor: 'rgb(59 130 246)', marginTop: '2rem' }}
      >
        <div className="mb-4 flex items-center">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="lucide lucide-briefcase mr-2 h-6 w-6"
            style={{ color: 'hsl(var(--primary))' }}
          >
            <path d="M16 20V4a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
            <rect width="20" height="14" x="2" y="6" rx="2"></rect>
          </svg>
          <h2 className="text-xl font-semibold">Order Summary</h2>
        </div>

        <div className="space-y-3">
          {selectedServices.map((service) => {
            const price = parseFloat(service.adjustment || '0');
            const quantity = service.quotedQuantity || 1;
            const totalPrice = price * quantity;
            const lineDiscount = getLineDiscount(
              totalPrice,
              discountPercentValue,
              discountCostValue,
              service.excludeInvoiceDiscount,
            );
            return (
              <div key={service.id} className="rounded border bg-white p-4">
                <div className="mb-3">
                  <div className="whitespace-pre-line break-words text-sm font-medium">
                    {service.description}
                  </div>
                </div>

                <div className="flex flex-wrap gap-6 text-sm">
                  <div>
                    <span className="text-gray-600">Qty:</span>
                    <span className="ml-2 font-medium">{quantity}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Unit Price:</span>
                    <span className="ml-2 font-medium">${price.toFixed(2)}</span>
                  </div>
                  {lineDiscount > 0 && (
                    <div>
                      <span className="font-medium text-green-700">
                        Discount -${lineDiscount.toFixed(2)}
                      </span>
                    </div>
                  )}
                  <div>
                    <span className="text-gray-600">Total:</span>
                    <span className="ml-2 font-semibold">${totalPrice.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            );
          })}

          <div className="space-y-2 rounded border border-gray-300 bg-gray-100 p-4">
            <div className="flex items-center justify-between text-sm">
              <span className="font-medium">Subtotal</span>
              <span className="font-medium">${subtotal.toFixed(2)}</span>
            </div>
            {(discountCostValue > 0 || discountPercentValue > 0) && discountAmount > 0 && (
              <div className="flex items-center justify-between text-sm text-gray-600">
                <span>{discountCostValue > 0 ? 'Discount' : 'Total discount'}</span>
                <span>-${discountAmount.toFixed(2)}</span>
              </div>
            )}
            {parsedTaxRate > 0 && (
              <div className="flex items-center justify-between text-sm text-gray-600">
                <span>Tax ({parsedTaxRate}%)</span>
                <span>${taxAmount.toFixed(2)}</span>
              </div>
            )}
            <div className="flex items-center justify-between border-t pt-2">
              <span className="text-lg font-bold">Grand Total</span>
              <span className="text-xl font-bold">${total.toFixed(2)}</span>
            </div>
          </div>
        </div>

        {/* Agreement Duration */}
        <div className="mt-6 rounded border p-4">
          <h3 className="mb-2 font-medium">Agreement Duration</h3>
          <p className="text-sm">
            This agreement is valid until {proposalValidity.agreementDuration.toLocaleDateString()}
          </p>
        </div>
      </Card>
    </div>
  );
};
